/**
 * CitationChip Component
 *
 * Reusable chip component that displays citation provenance
 * Shows: [doc_short_title, Sec X, p.Y] with tooltip
 */

import React from 'react';
import { Chip, Tooltip, useTheme } from '@mui/material';
import type { Citation, DocumentRegistry } from '../types';

interface CitationChipProps {
  citation: Citation;
  documentRegistry?: DocumentRegistry;
  size?: 'small' | 'medium';
}

export const CitationChip: React.FC<CitationChipProps> = ({
  citation,
  documentRegistry,
  size = 'small',
}) => {
  const theme = useTheme();

  // Get document short title from registry
  const docEntry = documentRegistry?.[citation.doc_id];
  const docTitle = docEntry?.short_title || citation.doc_id;

  // Build citation parts
  const parts: string[] = [docTitle];
  if (citation.section) {
    parts.push(citation.section);
  }
  if (citation.pages) {
    parts.push(`p.${citation.pages}`);
  }

  const citationText = parts.join(', ');
  const tooltipText = citation.quote || citationText;

  return (
    <Tooltip title={tooltipText} arrow>
      <Chip
        label={citationText}
        size={size}
        sx={{
          height: size === 'small' ? 20 : 24,
          fontSize: size === 'small' ? '0.65rem' : '0.75rem',
          bgcolor: theme.palette.grey[100],
          color: theme.palette.text.secondary,
          border: `1px solid ${theme.palette.grey[300]}`,
          '& .MuiChip-label': {
            px: 0.75,
          },
        }}
      />
    </Tooltip>
  );
};
